if (!customElements.get('mz-review-slider')) {
  class MzReviewSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-mz-review-track]');
      this.slides = Array.from(this.querySelectorAll('[data-mz-review-slide]'));
      this.previousButton = this.querySelector('[data-mz-review-prev]');
      this.nextButton = this.querySelector('[data-mz-review-next]');
      this.dotsContainer = this.querySelector('[data-mz-review-dots]');
      this.status = this.querySelector('[data-mz-review-status]');
      this.currentPage = 0;
      this.autoplayTimer = null;
      this.scrollFrame = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.track || this.slides.length < 2) return;

      this.onPrevious = () => this.goToPage(this.currentPage - 1);
      this.onNext = () => this.goToPage(this.currentPage + 1);
      this.onScroll = () => {
        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => this.syncFromScroll());
      };
      this.onPointerEnter = () => this.stopAutoplay();
      this.onPointerLeave = () => this.startAutoplay();
      this.onFocusIn = () => this.stopAutoplay();
      this.onFocusOut = () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.startAutoplay();
        }, 0);
      };

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('mouseenter', this.onPointerEnter);
      this.addEventListener('mouseleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);

      this.resizeObserver = new ResizeObserver(() => this.rebuildPagination());
      this.resizeObserver.observe(this.track);
      this.rebuildPagination();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('mouseenter', this.onPointerEnter);
      this.removeEventListener('mouseleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
    }

    visibleCount() {
      return window.matchMedia('(min-width: 750px)').matches ? Math.min(2, this.slides.length) : 1;
    }

    rebuildPagination() {
      const nextVisibleCount = this.visibleCount();
      const nextPageCount = Math.max(1, Math.ceil(this.slides.length / nextVisibleCount));

      if (nextVisibleCount === this.itemsPerPage && nextPageCount === this.pageCount) return;

      this.itemsPerPage = nextVisibleCount;
      this.pageCount = nextPageCount;
      this.currentPage = Math.min(this.currentPage, this.pageCount - 1);
      this.buildDots();
      this.goToPage(this.currentPage, false);
      this.startAutoplay();
    }

    buildDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.replaceChildren();
      this.dots = Array.from({ length: this.pageCount }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mz-review-slider__dot';
        dot.setAttribute('aria-label', `Bewertungsseite ${index + 1} von ${this.pageCount}`);
        dot.addEventListener('click', () => this.goToPage(index));
        this.dotsContainer.appendChild(dot);
        return dot;
      });
    }

    goToPage(page, smooth = true) {
      if (!this.track || !this.pageCount) return;

      const nextPage = Math.max(0, Math.min(page, this.pageCount - 1));
      const targetIndex = Math.min(nextPage * this.itemsPerPage, this.slides.length - 1);
      const target = this.slides[targetIndex];
      const targetLeft = target.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;

      this.currentPage = nextPage;
      this.track.scrollTo({
        left: targetLeft,
        behavior: smooth && !this.reduceMotion ? 'smooth' : 'auto',
      });
      this.updateControls();
    }

    syncFromScroll() {
      const pageWidth = Math.max(this.track.clientWidth, 1);
      const nextPage = Math.max(0, Math.min(Math.round(this.track.scrollLeft / pageWidth), this.pageCount - 1));

      if (nextPage !== this.currentPage) {
        this.currentPage = nextPage;
        this.updateControls();
      }
    }

    updateControls() {
      if (this.previousButton) this.previousButton.disabled = this.currentPage === 0;
      if (this.nextButton) this.nextButton.disabled = this.currentPage === this.pageCount - 1;

      this.dots?.forEach((dot, index) => {
        const isActive = index === this.currentPage;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.status) this.status.textContent = `Bewertungsseite ${this.currentPage + 1} von ${this.pageCount}`;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.dataset.autoplay !== 'true' || this.reduceMotion || this.pageCount < 2) return;

      const interval = Math.max(Number(this.dataset.interval) || 6000, 4000);
      this.autoplayTimer = window.setInterval(() => {
        const nextPage = this.currentPage + 1 >= this.pageCount ? 0 : this.currentPage + 1;
        this.goToPage(nextPage);
      }, interval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  customElements.define('mz-review-slider', MzReviewSlider);
}